import type {
  SpaceSwitchStage,
  SpaceSwitchRestoreMode,
  SpaceSwitchTransactionType,
} from "./spaceSwitchState";
import type {
  UnitySpaceNavigationOperation,
  UnitySpaceNavigationPhase,
  ViewRData,
} from "../../types/unity";

export const SPACE_NAV_TRANSACTION_STARTED_EVENT =
  "spaceNav:transactionStarted";
export const SPACE_NAV_RUNTIME_RESTORED_EVENT = "spaceNav:runtimeRestored";
export const SPACE_NAV_PROJECT_RELOADED_EVENT = SPACE_NAV_RUNTIME_RESTORED_EVENT;
export const SPACE_NAV_RESTORE_READY_EVENT = "spaceNav:restoreReady";
export const SPACE_NAV_SPACE_CONFIRMED_EVENT = "spaceNav:spaceConfirmed";
export const SPACE_NAV_VSE_MODEL_READY_EVENT = "spaceNav:vseModelReady";
export const SPACE_NAV_INTERACTIVE_READY_EVENT = "spaceNav:interactiveReady";
export const SPACE_NAV_SOFT_COMPLETED_EVENT = "spaceNav:softCompleted";
export const SPACE_NAV_STABILIZED_EVENT = "spaceNav:stabilized";
export const SPACE_NAV_COMPLETED_EVENT = "spaceNav:completed";
export const SPACE_NAV_ABORTED_EVENT = "spaceNav:aborted";

export type SpaceNavigationEventName =
  | typeof SPACE_NAV_TRANSACTION_STARTED_EVENT
  | typeof SPACE_NAV_RUNTIME_RESTORED_EVENT
  | typeof SPACE_NAV_RESTORE_READY_EVENT
  | typeof SPACE_NAV_SPACE_CONFIRMED_EVENT
  | typeof SPACE_NAV_VSE_MODEL_READY_EVENT
  | typeof SPACE_NAV_INTERACTIVE_READY_EVENT
  | typeof SPACE_NAV_SOFT_COMPLETED_EVENT
  | typeof SPACE_NAV_STABILIZED_EVENT
  | typeof SPACE_NAV_COMPLETED_EVENT
  | typeof SPACE_NAV_ABORTED_EVENT;

export interface SpaceNavigationEventDetail {
  transactionId: string | null;
  transactionType?: SpaceSwitchTransactionType | null;
  targetSpaceIndex?: number | null;
  targetSpaceId?: string | null;
  currentSpaceIndex?: number | null;
  confirmedSpaceIndex?: number | null;
  hasLoadableVseInCurrentSpace?: boolean;
  loadableVseCountInCurrentSpace?: number;
  runId?: number;
  restoreMode?: SpaceSwitchRestoreMode | null;
  operation?: UnitySpaceNavigationOperation | null;
  phase?: UnitySpaceNavigationPhase | null;
  viewrDataRevision?: number | null;
  projectIdentity?: string | null;
  expectsExplicitSpaceSelect?: boolean | null;
  selectionCommandAccepted?: boolean | null;
  selectionCommandRejectedReason?: string | null;
  reason?: string;
  backgroundVerificationRequired?: boolean;
  backgroundVerification?: boolean;
  status?: string;
  stage?: SpaceSwitchStage | null;
  source?: string;
}

export function normalizeUnityIframeMessageData(
  data: unknown,
): Record<string, unknown> | null {
  if (!data || typeof data !== "object") return null;

  const outer = data as Record<string, unknown>;
  if (outer.type !== "UNITY_TO_REACT") return outer;

  const message = outer.message;
  if (message && typeof message === "object") {
    return message as Record<string, unknown>;
  }
  if (typeof message !== "string") return null;

  try {
    const parsed = JSON.parse(message);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function dispatchSpaceNavigationEvent(
  name: SpaceNavigationEventName,
  detail: SpaceNavigationEventDetail,
): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // ignore
  }
}

export function subscribeSpaceNavigationEvent(
  name: SpaceNavigationEventName,
  listener: (event: CustomEvent<SpaceNavigationEventDetail>) => void,
  options?: AddEventListenerOptions,
): () => void {
  const wrapped = listener as EventListener;
  window.addEventListener(name, wrapped, options);
  return () => {
    window.removeEventListener(name, wrapped, options);
  };
}

export function subscribeSpaceNavigation(
  listener: (event: CustomEvent<SpaceNavigationEventDetail>) => void,
  transactionId?: string | null,
): () => void {
  const wrapped = (event: Event) => {
    const customEvent = event as CustomEvent<SpaceNavigationEventDetail>;
    if (
      transactionId &&
      customEvent.detail?.transactionId &&
      customEvent.detail.transactionId !== transactionId
    ) {
      return;
    }
    listener(customEvent);
  };

  window.addEventListener(
    SPACE_NAV_TRANSACTION_STARTED_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_RUNTIME_RESTORED_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_RESTORE_READY_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_SPACE_CONFIRMED_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_VSE_MODEL_READY_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_INTERACTIVE_READY_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_SOFT_COMPLETED_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_STABILIZED_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(
    SPACE_NAV_COMPLETED_EVENT,
    wrapped as EventListener,
  );
  window.addEventListener(SPACE_NAV_ABORTED_EVENT, wrapped as EventListener);

  return () => {
    window.removeEventListener(
      SPACE_NAV_TRANSACTION_STARTED_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_RUNTIME_RESTORED_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_RESTORE_READY_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_SPACE_CONFIRMED_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_VSE_MODEL_READY_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_INTERACTIVE_READY_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_SOFT_COMPLETED_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_STABILIZED_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_COMPLETED_EVENT,
      wrapped as EventListener,
    );
    window.removeEventListener(
      SPACE_NAV_ABORTED_EVENT,
      wrapped as EventListener,
    );
  };
}

export function subscribeUnitySpaceNavigationMessage(
  listener: (detail: SpaceNavigationEventDetail) => void,
  getExpectedSource?: () => Window | null,
): () => void {
  const handleMessage = (event: MessageEvent) => {
    const expectedSource = getExpectedSource?.();
    if (expectedSource && event.source && event.source !== expectedSource) return;
    const data = normalizeUnityIframeMessageData(event.data);
    if (data?.type !== "unityToReact_spaceNavigationState") return;
    const payload = data.payload;
    if (!payload || typeof payload !== "object") return;
    listener(payload as SpaceNavigationEventDetail);
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}

export function subscribeUnityViewRDataMessage(
  listener: (viewRData: ViewRData) => void,
): () => void {
  const handleMessage = (event: MessageEvent) => {
    const data = normalizeUnityIframeMessageData(event.data);
    if (data?.type !== "unityToReact_viewrDataUpdated") return;
    let payload = data.payload ?? data.data ?? data.viewRData;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        return;
      }
    }
    const payloadObject =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : null;
    const nestedViewRData = payloadObject?.viewRData;
    const viewRData =
      nestedViewRData && typeof nestedViewRData === "object"
        ? (nestedViewRData as Record<string, unknown>)
        : payloadObject;
    if (!viewRData || !Array.isArray(viewRData.spaces)) return;
    listener(viewRData as unknown as ViewRData);
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}
